import { Router, createRouter, createWebHashHistory } from 'vue-router';
import { getToken, getAppId } from '@/common/utils/index';
import { updateFavicon } from '@/common/utils/favicon';
import { useLayoutStore } from '@/store';
import { Dialog } from '@/components/Dialog';
import { ANY_OBJECT } from '@/types/generic';
import { recordJumpBackstageInfo } from '@/api/requset/tOperationLog';
import { routers } from './systemRouters';

// 第三方页面路由跳转映射
const thirdRouteMap: ANY_OBJECT = {
  handlerFlowTask: 'thirdHandlerFlowTask',
};

// 创建路由实例
export const router: Router = createRouter({
  history: createWebHashHistory(), //createWebHistory(),
  routes: routers,
  scrollBehavior(to, from, savedPosition) {
    return new Promise(resolve => {
      if (savedPosition) {
        return savedPosition;
      }
      if (from.meta.saveSrollTop) {
        const top: number = document.documentElement.scrollTop || document.body.scrollTop;
        resolve({ left: 0, top });
      }
    });
  },
});

/**
 * 判断是否为后台模块路由（/ht/**）
 * @param path 完整路由路径
 * @returns 是否匹配 /ht 或以 /ht/ 开头
 */
function isHtRoutePath(path: string): boolean {
  return path === '/ht' || path.startsWith('/ht/');
}

// 融合自 index.js 的动态导入错误处理逻辑
// Workaround for https://github.com/vitejs/vite/issues/11804
router.onError((err, to) => {
  // 静默处理 Navigation aborted/cancelled 错误（如 iframe hash 变化触发的误导航）
  if (
    err?.message?.includes?.('Navigation aborted') ||
    err?.message?.includes?.('Navigation cancelled') ||
    err?.type === 8 /* NavigationFailureType.aborted */ ||
    err?.type === 16 /* NavigationFailureType.cancelled */
  ) {
    return;
  }
  if (err?.message?.includes?.('Failed to fetch dynamically imported module')) {
    if (!localStorage.getItem('vuetify:dynamic-reload')) {
      console.log('Reloading page to fix dynamic import error');
      localStorage.setItem('vuetify:dynamic-reload', 'true');
      location.assign(to.fullPath);
    } else {
      console.error('Dynamic import error, reloading page did not fix it', err);
    }
  } else {
    console.error(err);
  }
});

router.beforeEach((to, from, next) => {
  if (to.query.from) {
    console.log('from 参数的值:', to.query.from);
    localStorage.setItem('from', to.query.from as string);
    updateFavicon();
  }
  console.log('[路由守卫] to1:', to);
  if (to.name === 'home') {
    console.log('[路由守卫] to2:', to);
    next();
  } else if (to.name === 'login2') {
    next({ name: 'login' });
  } else if (to.path.startsWith('/main/')) {
    next({ name: 'login' });
  } else if (to.path.indexOf('/thirdParty/') !== -1) {
    console.log('[路由守卫] to3:', to);
    // 第三方接入URL
    next();
  } else if (to.path === '/yuanqu' || to.name === 'yuanqu') {
    // yuanqu 页面直接放行
    next();
  } else if (to.name === 'NotFound') {
    // iframe 内部 hash 路由变化触发主项目路由跳转，阻止
    next(false);
  } else {
    next();
    console.log('[路由守卫] to4:', to);
    // 取token信息判断是否登录
    const token = getToken();
    const toName: string = to.name as string;
    if (!token || !/\S/.test(token)) {
      Dialog.closeAll();
      // next({ name: 'login' });
      next();
    } else if (from.path.indexOf('/thirdParty/') !== -1 && thirdRouteMap[toName]) {
      // 第三方接入跳转页面，需要跳转到第三方的路由上
      let url =
        location.origin +
        location.pathname +
        '#/thirdParty/' +
        thirdRouteMap[toName] +
        '?appId=' +
        getAppId() +
        '&token=' +
        getToken();
      url += '&thirdParamsString=' + encodeURIComponent(JSON.stringify(to.query));
      location.href = url;
    } else {
      if (from.meta.refreshParentCachedPage) {
        const layoutStore = useLayoutStore();
        layoutStore.removeCachePage(toName);
        from.meta.refreshParentCachedPage = false;
      }
      next();
    }
  }
});

/**
 * 路由切换后：从非后台模块进入后台模块时，上报跳转后台信息
 * @param to 目标路由
 * @param from 来源路由
 */
router.afterEach(async (to, from) => {
  if (!isHtRoutePath(from.path) && isHtRoutePath(to.path)) {
    console.log('[路由上报] 进入后台模块，调用记录接口');
    try {
      recordJumpBackstageInfo();
    } catch (e) {}
  }
});

// 融合自 index.js 的路由就绪后清理逻辑
router.isReady().then(() => {
  localStorage.removeItem('vuetify:dynamic-reload');
});

export default router;
